// The Code workspace's centre panel: open-file tabs and the run/format
// actions on one row, the Monaco editor filling the middle, and a status
// bar underneath (problems, caret, indentation, language, save state) -
// the layout Godot's script editor and Roblox Studio's Script Editor both
// converge on, and the one every creator who has used any code editor
// already knows.
//
// Saving is automatic (scriptStore autosaves 1.2s after the last edit) and
// Ctrl/Cmd+S saves now; the status bar says which state the file is in.
// Running never needs Preview: Ctrl/Cmd+Enter runs the file in a sandbox
// (codeRunStore) and the Output panel shows what happened, immediately.
import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';

import { useTheme } from '../../design/theme';
import { Link, navigate } from '../../design/router';
import { useToast } from '../../design/toast';
import { useEditorHost } from '../EditorHost';
import { useCodeRunStore } from '../scripting/codeRunStore';
import { triggerEditorAction, useEditorCommands } from '../scripting/editorCommands';
import { HollowScriptFileEditor } from '../scripting/monaco/HollowScriptFileEditor';
import { countProblems, useScriptProblemsStore, useScriptProblemsSync } from '../scripting/scriptProblemsStore';
import { isScriptDirty, useScriptStore } from '../../scripting/scriptStore';
import { CodeIcon } from './codeIcons';
import { createScriptFromTemplate, NewScriptMenu } from './CodeFileListPanel';
import { CwMenu, CwMenuDivider, CwMenuHeading, CwMenuItem, CwToolButton, MOD_KEY, ScriptFileName } from './codeShared';
import { SCRIPT_TEMPLATES } from './scriptTemplates';
import { useProjectScriptCapabilities } from './useProjectScriptCapabilities';

export interface EditorPrefs {
  readonly wordWrap: boolean;
  readonly minimap: boolean;
  readonly fontSize: number;
}

const DEFAULT_PREFS: EditorPrefs = { wordWrap: false, minimap: true, fontSize: 13 };
const PREFS_KEY = 'ss-code-editor-prefs';
const FONT_SIZES = [11, 12, 13, 14, 15, 16, 18];

function loadPrefs(): EditorPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<EditorPrefs>;
    return {
      wordWrap: typeof parsed.wordWrap === 'boolean' ? parsed.wordWrap : DEFAULT_PREFS.wordWrap,
      minimap: typeof parsed.minimap === 'boolean' ? parsed.minimap : DEFAULT_PREFS.minimap,
      fontSize: FONT_SIZES.includes(parsed.fontSize as number) ? (parsed.fontSize as number) : DEFAULT_PREFS.fontSize,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function usePrefs(): [EditorPrefs, (patch: Partial<EditorPrefs>) => void] {
  const [prefs, setPrefs] = useState<EditorPrefs>(loadPrefs);
  const update = useCallback((patch: Partial<EditorPrefs>) => {
    setPrefs((current) => {
      const next = { ...current, ...patch };
      try {
        localStorage.setItem(PREFS_KEY, JSON.stringify(next));
      } catch {
        /* a blocked localStorage just means the preference does not persist */
      }
      return next;
    });
  }, []);
  return [prefs, update];
}

function EmptyState({ hasScripts, onOpenFirst }: { hasScripts: boolean; onOpenFirst: () => void }) {
  const featured = SCRIPT_TEMPLATES.filter((t) => t.id !== 'blank').slice(0, 4);
  return (
    <div className="cw-empty">
      <div className="cw-empty-card">
        <div>
          <div className="cw-empty-title">{hasScripts ? 'Nothing open' : 'Write your first script'}</div>
          <p className="cw-empty-lead" style={{ marginTop: 8 }}>
            {hasScripts ? (
              'Pick a script from the list on the left, or start a new one.'
            ) : (
              <>
                HollowScript is Soulstack&apos;s own language for game logic: statically typed, safe by design, and quick to read. A script says what it
                touches with <code>uses</code>, marks each of those calls with <code>effect</code>, and runs in a sandbox the moment you press{' '}
                <code>{MOD_KEY}+Enter</code>.
              </>
            )}
          </p>
        </div>
        {hasScripts ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="ss-btn ss-btn--primary ss-btn--sm" onClick={onOpenFirst}>Open a script</button>
          </div>
        ) : (
          <>
            <div className="cw-template-grid">
              {featured.map((template) => (
                <button key={template.id} type="button" className="cw-template" onClick={() => createScriptFromTemplate(template)}>
                  <span className="cw-template-label" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ color: 'var(--ss-accent)', display: 'flex' }}>
                      <CodeIcon kind={template.runOnStart ? 'bolt' : 'terminal'} size={14} />
                    </span>
                    {template.label}
                  </span>
                  <span className="cw-template-desc">{template.description}</span>
                </button>
              ))}
            </div>
            <div className="cw-empty-foot">
              Or start from a{' '}
              <button type="button" className="ss-btn ss-btn--ghost" style={{ padding: '3px 9px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7 }} onClick={() => createScriptFromTemplate(SCRIPT_TEMPLATES[0]!)}>
                blank file
              </button>
              . The Reference panel on the right lists every capability, function and keyword you can use.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function CodeEditorPanel() {
  const { theme } = useTheme();
  const { editorBase } = useEditorHost();
  const notify = useToast();
  const scripts = useScriptStore((s) => s.scripts);
  const sourceScripts = useScriptStore((s) => s.sourceScripts);
  const activeScriptId = useScriptStore((s) => s.activeScriptId);
  const openScriptIds = useScriptStore((s) => s.openScriptIds);
  const savingIds = useScriptStore((s) => s.savingIds);
  const saveState = useScriptStore((s) => s.saveState);
  const statusMessage = useScriptStore((s) => s.statusMessage);
  const setActiveSource = useScriptStore((s) => s.setActiveSource);
  const openScript = useScriptStore((s) => s.openScript);
  const closeScript = useScriptStore((s) => s.closeScript);
  const saveScript = useScriptStore((s) => s.saveScript);
  const setRunOnStart = useScriptStore((s) => s.setRunOnStart);
  const duplicateScript = useScriptStore((s) => s.duplicateScript);
  const flushPendingSaves = useScriptStore((s) => s.flushPendingSaves);

  const { capabilities } = useProjectScriptCapabilities();
  useScriptProblemsSync(capabilities);
  const problemsByScript = useScriptProblemsStore((s) => s.byScript);

  const running = useCodeRunStore((s) => s.running);
  const lastRun = useCodeRunStore((s) => s.lastRun);
  const run = useCodeRunStore((s) => s.run);
  const stop = useCodeRunStore((s) => s.stop);
  const cursor = useEditorCommands((s) => s.cursor);

  const [prefs, updatePrefs] = usePrefs();
  const [moreOpen, setMoreOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  const active = scripts.find((s) => s.id === activeScriptId);
  const dirty = !!active && isScriptDirty({ scripts, sourceScripts }, active.id);
  const saving = !!active && savingIds.has(active.id);
  const openScripts = useMemo(() => openScriptIds.map((id) => scripts.find((s) => s.id === id)).filter((s): s is NonNullable<typeof s> => s !== undefined), [openScriptIds, scripts]);
  const activeCounts = countProblems(active ? problemsByScript[active.id] : undefined);

  // Leaving the page mid-debounce must not lose the last edit.
  useEffect(() => {
    const onHide = () => void flushPendingSaves();
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      void flushPendingSaves();
    };
  }, [flushPendingSaves]);

  // A failed save is the one thing here worth interrupting for.
  useEffect(() => {
    if (saveState === 'error' && statusMessage) notify(`Script didn't save: ${statusMessage}`, 'error');
  }, [saveState, statusMessage, notify]);

  const runActive = useCallback(() => {
    const state = useScriptStore.getState();
    const script = state.scripts.find((s) => s.id === state.activeScriptId);
    if (!script) return;
    // Save first so Preview's run-on-start and imports see the same code
    // the sandbox just ran - fire and forget, the run does not wait on it.
    void state.saveScript(script.id);
    void run(script, state.scripts, capabilities);
  }, [run, capabilities]);

  const saveActive = useCallback(() => {
    const id = useScriptStore.getState().activeScriptId;
    if (id) void saveScript(id);
  }, [saveScript]);

  function onTabMouseDown(e: MouseEvent, id: string) {
    if (e.button === 1) {
      e.preventDefault();
      closeScript(id);
    }
  }

  const saveLabel = saving ? 'Saving…' : dirty ? 'Unsaved · autosaves' : saveState === 'error' ? 'Save failed' : 'Saved';

  return (
    <div className="cw-fill">
      <div className="cw-tabbar">
        <div className="cw-tabs" role="tablist" aria-label="Open scripts">
          {openScripts.map((script) => {
            const isActive = script.id === activeScriptId;
            const tabDirty = isScriptDirty({ scripts, sourceScripts }, script.id);
            const counts = countProblems(problemsByScript[script.id]);
            return (
              <button
                key={script.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={['cw-tab', isActive && 'is-active', tabDirty && 'is-dirty', counts.errors > 0 && 'has-error'].filter(Boolean).join(' ')}
                onClick={() => openScript(script.id)}
                onMouseDown={(e) => onTabMouseDown(e, script.id)}
                title={`${script.name}.hws${tabDirty ? ' · unsaved edits' : ''}${counts.errors ? ` · ${counts.errors} error${counts.errors === 1 ? '' : 's'}` : ''}`}
              >
                {script.runOnStart && <span style={{ display: 'flex', color: isActive ? 'var(--ss-accent)' : 'inherit' }}><CodeIcon kind="bolt" size={12} /></span>}
                <ScriptFileName name={script.name} className="cw-tab-name" />
                <span
                  className="cw-tab-close"
                  role="button"
                  aria-label={`Close ${script.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeScript(script.id);
                  }}
                >
                  <span className="cw-dot" />
                  <CodeIcon kind="close" size={11} />
                </span>
              </button>
            );
          })}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', padding: '0 4px' }}>
            <CwToolButton icon="plus" label="New script" showLabel={false} onClick={() => setNewOpen((o) => !o)} active={newOpen} />
            <NewScriptMenu open={newOpen} onClose={() => setNewOpen(false)} align="left" />
          </div>
        </div>
        <div className="cw-actions">
          {running ? (
            <CwToolButton icon="stop" label="Stop" danger onClick={stop} title="Stop the running script" />
          ) : (
            <CwToolButton icon="play" label="Run" primary onClick={runActive} disabled={!active} shortcut={`${MOD_KEY}+↵`} title={`Run this script in the sandbox (${MOD_KEY}+Enter)`} />
          )}
          <span className="cw-toolbar-divider" />
          <CwToolButton icon="format" label="Format" showLabel={false} onClick={() => triggerEditorAction('editor.action.formatDocument')} disabled={!active} shortcut="Shift+Alt+F" />
          <CwToolButton icon="search" label="Find" showLabel={false} onClick={() => triggerEditorAction('actions.find')} disabled={!active} shortcut={`${MOD_KEY}+F`} />
          <CwToolButton icon="symbol" label="Go to symbol" showLabel={false} onClick={() => triggerEditorAction('editor.action.quickOutline')} disabled={!active} shortcut={`${MOD_KEY}+Shift+O`} />
          <div style={{ position: 'relative', display: 'flex' }}>
            <CwToolButton icon="more" label="More" showLabel={false} onClick={() => setMoreOpen((o) => !o)} active={moreOpen} />
            <CwMenu open={moreOpen} onClose={() => setMoreOpen(false)} width={250}>
              {active && (
                <>
                  <CwMenuHeading>{active.name}.hws</CwMenuHeading>
                  <CwMenuItem
                    label="Run on start"
                    hint="Runs automatically when Preview or the game starts."
                    checked={active.runOnStart}
                    onClick={() => setRunOnStart(active.id, !active.runOnStart)}
                  />
                  <CwMenuItem icon="copy" label="Duplicate" onClick={() => { setMoreOpen(false); duplicateScript(active.id); }} />
                  <CwMenuItem icon="check" label="Save now" shortcut={`${MOD_KEY}+S`} onClick={() => { setMoreOpen(false); saveActive(); }} disabled={!dirty} />
                  <CwMenuDivider />
                </>
              )}
              <CwMenuHeading>Editor</CwMenuHeading>
              <CwMenuItem label="Word wrap" checked={prefs.wordWrap} onClick={() => updatePrefs({ wordWrap: !prefs.wordWrap })} />
              <CwMenuItem label="Minimap" checked={prefs.minimap} onClick={() => updatePrefs({ minimap: !prefs.minimap })} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 9px' }}>
                <span style={{ fontSize: 'var(--ss-fs-sm)', flex: 1 }}>Font size</span>
                <button type="button" className="cw-tool is-icon" aria-label="Smaller text" disabled={prefs.fontSize <= FONT_SIZES[0]!} onClick={() => updatePrefs({ fontSize: FONT_SIZES[Math.max(0, FONT_SIZES.indexOf(prefs.fontSize) - 1)]! })}>
                  −
                </button>
                <span style={{ fontFamily: 'var(--ss-mono)', fontSize: 12, minWidth: 22, textAlign: 'center' }}>{prefs.fontSize}</span>
                <button type="button" className="cw-tool is-icon" aria-label="Larger text" disabled={prefs.fontSize >= FONT_SIZES[FONT_SIZES.length - 1]!} onClick={() => updatePrefs({ fontSize: FONT_SIZES[Math.min(FONT_SIZES.length - 1, FONT_SIZES.indexOf(prefs.fontSize) + 1)]! })}>
                  +
                </button>
              </div>
              <CwMenuDivider />
              <CwMenuItem icon="play" label="Open Preview" hint="Runs every Run-on-start script against the live scene." onClick={() => { setMoreOpen(false); navigate(`${editorBase}/preview`); }} />
            </CwMenu>
          </div>
        </div>
      </div>

      <div className="cw-editor-body">
        {active ? (
          <HollowScriptFileEditor
            fileId={active.id}
            fileName={active.name}
            value={active.source}
            onChange={setActiveSource}
            onRunRequested={runActive}
            onSaveRequested={saveActive}
            capabilities={capabilities}
            theme={theme}
            wordWrap={prefs.wordWrap}
            minimap={prefs.minimap}
            fontSize={prefs.fontSize}
          />
        ) : (
          <EmptyState hasScripts={scripts.length > 0} onOpenFirst={() => scripts[0] && openScript(scripts[0].id)} />
        )}
      </div>

      <div className="cw-status" role="status">
        <button
          type="button"
          className={'cw-status-item is-button' + (activeCounts.errors > 0 ? ' is-error' : activeCounts.warnings > 0 ? ' is-warning' : '')}
          onClick={() => navigate(`${editorBase}/code.problems`)}
          title="Open the Problems panel"
          disabled={!active}
        >
          <CodeIcon kind={activeCounts.errors > 0 ? 'error' : activeCounts.warnings > 0 ? 'warning' : 'check'} size={12} />
          {active
            ? activeCounts.errors + activeCounts.warnings === 0
              ? 'No problems'
              : [activeCounts.errors > 0 && `${activeCounts.errors} error${activeCounts.errors === 1 ? '' : 's'}`, activeCounts.warnings > 0 && `${activeCounts.warnings} warning${activeCounts.warnings === 1 ? '' : 's'}`].filter(Boolean).join(', ')
            : 'No file open'}
        </button>
        {running && (
          <span className="cw-status-item is-accent">
            <CodeIcon kind="play" size={11} /> Running {running.scriptName}.hws
          </span>
        )}
        {!running && lastRun && active && lastRun.scriptId === active.id && (
          <span className={'cw-status-item ' + (lastRun.ok ? 'is-success' : 'is-error')} title="Last run">
            <CodeIcon kind={lastRun.ok ? 'check' : 'error'} size={11} /> Last run {lastRun.ok ? 'finished' : 'failed'} in {lastRun.durationMs} ms
          </span>
        )}
        <span className="cw-status-spacer" />
        {active && cursor && <span className="cw-status-item">Ln {cursor.line}, Col {cursor.column}</span>}
        {active && <span className="cw-status-item" title="The HollowScript formatter indents with four spaces">Spaces: 4</span>}
        <span className="cw-status-item">HollowScript</span>
        <span className={'cw-status-item' + (saveState === 'error' ? ' is-error' : dirty || saving ? '' : ' is-success')} title={dirty ? 'Edits save automatically a moment after you stop typing' : undefined}>
          {active ? saveLabel : ''}
        </span>
        <Link to={`${editorBase}/preview`} className="cw-status-item is-button" title="Open Preview: every Run-on-start script runs against the live scene">
          <CodeIcon kind="play" size={10} /> Preview
        </Link>
      </div>
    </div>
  );
}
